import dayjs from 'dayjs';

import { ALWAYS_OPEN_TIME, DAYS_OF_THE_WEEK, MIDNIGHT } from 'constants/menuManagement';
import { capitalize, generateTempId } from 'helpers/utils';
import { isEndTimePastMidnight } from 'helpers/menuManagementHours';
import { DayOption, TimeOption } from 'components/MenuManagement/MenuEdit/Hours/types';

interface Schedule {
  dayIndex: string;
  startTime: string;
  endTime: string;
  isPeriod?: boolean;
  isDeleted?: boolean;
  uuid?: string;
  tempId?: string;
  appliedDate?: string; // for special/holiday hours
}

interface HourMark {
  value: number;
  label: string;
}

interface HourLabel {
  hour: number;
  displayHour: number;
}

// Total minutes in a day minus one (1439 or 11:59pm)
export const LAST_MINUTE_OF_DAY = 24 * 60 - 1;
export const END_OF_DAY_TIME = '23:59';

export const timeToNumber = (timeString: string): number => {
  if (timeString === END_OF_DAY_TIME) {
    return LAST_MINUTE_OF_DAY;
  }
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

export const numberToTime = (value: number): string => {
  if (value >= LAST_MINUTE_OF_DAY) {
    return END_OF_DAY_TIME;
  }
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return dayjs().hour(hours).minute(minutes).format('HH:mm');
};

export const formatTimeLabel = (value: number): string => {
  if (value >= LAST_MINUTE_OF_DAY) {
    return '11:59 PM';
  }
  return dayjs(numberToTime(value), 'HH:mm').format('h:mm A');
};

export const getHourMarks = (): HourMark[] =>
  Array.from({ length: 24 }, (_, hour) => ({
    value: hour * 60,
    label: '',
  }));

export const getHourLabels = (): HourLabel[] =>
  Array.from({ length: 24 }, (_, hour) => ({
    hour,
    displayHour: hour % 12 || 12,
  }));

export function getTimeOptions(): TimeOption[] {
  const array: Date[] = [];
  let d = new Date();
  d.setHours(0, 0, 0, 0);
  for (let i = 0; i < 288; i += 1) {
    array.push(d);
    d = new Date(d.getTime() + 5 * 60 * 1000); // 5 minutes in milliseconds
  }
  return array.map(time => ({
    displayValue: dayjs(time).format('h:mm A'),
    value: dayjs(time).format('HH:mm'),
  }));
}

export function getDayOptions(): DayOption[] {
  const dayOptions: DayOption[] = [];
  Object.values(DAYS_OF_THE_WEEK).forEach(day => {
    dayOptions.push({ displayValue: capitalize(day.slice(0, 3)), value: day });
  });
  return dayOptions;
}

export function isDayClosed(schedule: Schedule): boolean {
  return schedule.startTime === MIDNIGHT && schedule.endTime === MIDNIGHT;
}

export function isDayOpened24Hours(schedule: Schedule): boolean {
  return schedule.startTime === MIDNIGHT && schedule.endTime === ALWAYS_OPEN_TIME;
}

interface GetScheduleTimeValueOptions {
  isEndTime?: boolean;
}

export function getScheduleTimeValue(
  schedule: Schedule,
  timeOptions: TimeOption[],
  { isEndTime = false }: GetScheduleTimeValueOptions = {},
): string {
  if (isDayClosed(schedule)) return 'Closed';
  if (isEndTime && isDayOpened24Hours(schedule)) return '11:59 PM';

  const time = isEndTime ? schedule.endTime : schedule.startTime;
  // Convert 24:00 to 23:59 for end time
  const adjustedTime = isEndTime && time === '24:00' ? '23:59' : time;
  const matchingOption = timeOptions.find(
    option => option.value === dayjs(adjustedTime, 'H:mm').format('HH:mm'),
  );

  return matchingOption?.displayValue || dayjs(adjustedTime, 'H:mm').format('h:mm A');
}

export function hasMoreThanOneDay(day: string, schedules: Schedule[]): boolean {
  return schedules.filter(schedule => schedule.dayIndex === day).length > 1;
}

export function canAddMoreHours(schedules?: Schedule[]): boolean {
  // if response returns an empty array without any schedules, we should be able to add hours
  if (!schedules?.length) return true;
  // otherwise can only add hours if at least one day is not closed and not open 24 hours
  return schedules.some(schedule => !isDayClosed(schedule) && !isDayOpened24Hours(schedule));
}

export function hasRolloverHours(schedule: Schedule): boolean {
  return isEndTimePastMidnight({ start: schedule.startTime, end: schedule.endTime });
}

export function getNextDayIndex(dayIndex: string): string {
  const days = Object.values(DAYS_OF_THE_WEEK);
  const currentIndex = days.findIndex(day => day === dayIndex);
  return days[(currentIndex + 1) % days.length];
}

interface SplitRolloverHoursOptions {
  forBackend?: boolean;
  isSpecial?: boolean;
}

// Split schedules that cross midnight (e.g. 9pm-2am) into two connected schedules
// forBackend: true = create new records for BE saving
// forBackend: false = link visual schedules for FE display
export function splitRolloverHours(
  schedule: Schedule,
  { forBackend = false, isSpecial = false }: SplitRolloverHoursOptions = {},
): Schedule[] {
  if (!hasRolloverHours(schedule)) {
    return [schedule];
  }

  const currentDaySchedule: Schedule = {
    ...schedule,
    endTime: END_OF_DAY_TIME,
  };

  // dont need a tempId for BE
  const baseSchedule = forBackend ? {} : schedule;

  let tempId: string | undefined;
  if (forBackend) {
    tempId = generateTempId();
  } else if (schedule.tempId) {
    tempId = `${schedule.tempId}-next`;
  }

  const nextDaySchedule: Schedule = {
    ...baseSchedule,
    startTime: MIDNIGHT,
    endTime: schedule.endTime,
    tempId,
    // When forBackend is true, preserve the original schedule's isPeriod value.
    // When forBackend is false, always mark the next day schedule as a period to show
    // the next day schedule as a period in timeline view
    isPeriod: forBackend ? schedule.isPeriod : true,
    // isSpecial = holiday hours, handled differently
    // because holiday hours are sent with appliedDate
    ...(isSpecial
      ? {
          dayIndex: schedule.dayIndex,
          appliedDate: dayjs(schedule.appliedDate).add(1, 'day').format('YYYY-MM-DD'),
        }
      : { dayIndex: getNextDayIndex(schedule.dayIndex) }),
  };

  return [currentDaySchedule, nextDaySchedule];
}

// Process all schedules and split any that cross midnight into separate day schedules
export function splitAllSchedulesRolloverHours(schedules: Schedule[]): Schedule[] {
  // for now we're keeping rollover hours as a single schedule until we decide on BE handling
  return schedules;
}

export function resolvePrimaryId(schedule: Schedule, isPeriod = false): string {
  const defaultId = `default-${schedule.dayIndex}`;
  return isPeriod
    ? schedule.uuid || schedule.tempId || ''
    : schedule.uuid || schedule.tempId || defaultId;
}
